import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard';

import { concatWithDot } from 'helpers/string';

import {
  IProjectedBalanceSheetsValuesType,
  projectedBalanceSheetsOptionsToComponentMap,
  requestsPrefix,
  useGetProjectedBalanceSheetsTabs,
} from './utils/form';
import { FinancialPlanChipSelector } from '../components/FinancialPlanChipSelector';
import { ActiveTabContextProvider, useTabChangeContext } from '../hooks/useTabChangeContext';
import { useGetInputFieldsList } from '../ProjectedCashFlow/utils/form';

import styles from './ProjectedBalanceSheets.module.scss';

export const ProjectedBalanceSheetsContent: FC = () => {
  const { t } = useTranslation();
  const PROJECTED_BALANCE_SHEETS_TABS = useGetProjectedBalanceSheetsTabs();
  const INPUT_FIELDS_LIST = useGetInputFieldsList();

  const { control } = useFormContext();
  const { activeTab, onTabChange } = useTabChangeContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.PROFIT_AND_LOSS_PROJECTION,
  );

  const Component = projectedBalanceSheetsOptionsToComponentMap[activeTab.value as IProjectedBalanceSheetsValuesType];

  return (
    <>
      <FormSectionCard disabled={!canAccess}>
        <FormHeader title={t('financialPlan.title')} badgeText={t('financialPlan.projectedBalanceSheets.title')} />
      </FormSectionCard>

      <FormSectionCard>
        <Box className={styles.wrapper}>
          <FinancialPlanChipSelector
            options={PROJECTED_BALANCE_SHEETS_TABS}
            selected={activeTab}
            onChange={onTabChange}
            wrapWithSectionCard={false}
          />
        </Box>
      </FormSectionCard>

      <Component />

      {INPUT_FIELDS_LIST.map(({ title, name }) => {
        return (
          <FormSectionCard
            disabled={!canAccess}
            title={title}
            key={title}
            aiFieldName={concatWithDot(requestsPrefix, name)}
          >
            <TextInputControl
              name={concatWithDot(requestsPrefix, name)}
              control={control}
              multiline
              minRows={3}
              maxLength={1500}
            />
          </FormSectionCard>
        );
      })}
    </>
  );
};

export const ProjectedBalanceSheets: FC = () => {
  const PROJECTED_BALANCE_SHEETS_TABS = useGetProjectedBalanceSheetsTabs();
  return (
    <ActiveTabContextProvider initialValue={PROJECTED_BALANCE_SHEETS_TABS[0]}>
      <ProjectedBalanceSheetsContent />
    </ActiveTabContextProvider>
  );
};
